import { z } from 'zod';
// Shared enums (03-database.md §1). The database stores them as VARCHAR(32)
// with a CHECK constraint built from these same lists, so adding a value is a
// data change here plus a migration that rewrites the CHECK, never an ALTER of
// a MySQL ENUM.
export const TRANSPORT_MODES = ['sea', 'air', 'road'];
export const DATA_SOURCES = ['carrier', 'carrier_schedule', 'ais', 'inferred', 'manual', 'dokless'];
export const DATA_STATUSES = ['actual', 'planned', 'estimated', 'unknown'];
export const SHIPMENT_STATUSES = [
    'received',
    'validating',
    'blocked',
    'scheduled',
    'pre_departure',
    'in_transit',
    'at_transshipment',
    'arrived',
    'closed',
];
export const CARGO_STATES = ['at_origin', 'on_board', 'at_transshipment', 'discharged'];
export const CLOSED_REASONS = [
    'arrived',
    'delivered',
    'canceled',
    'stale',
    'reused_container',
    'removed_by_dokless',
    'manual',
];
export const LEG_STATUSES = ['planned', 'active', 'done', 'unknown'];
export const LOCATION_KINDS = ['seaport', 'airport', 'inland', 'unknown'];
export const CONVEYANCE_KINDS = ['vessel', 'aircraft', 'truck'];
export const CARRIER_SEARCH_KEYS = ['container', 'bl', 'booking'];
/**
 * A carrier's standing (ADR 0087): `active` is read; `to_verify` was created for a Dokless code nobody knew and
 * waits for the owner's review of the research; `rejected` the owner said no. Only `active` carriers are read.
 */
export const CARRIER_STATUSES = ['active', 'to_verify', 'rejected'];
/** How a Dokless carrier code was tied to a Plancia carrier (`carrier_dokless_codes.matched_by`, ADR 0087). */
export const CARRIER_MATCHES = [
    'code',
    'alternative_code',
    'scac',
    'name',
    'shipsgo_name',
    'owner',
    'created',
];
/** A carrier research by Claude with web search (`carrier_research.status`, ADR 0087). */
export const CARRIER_RESEARCH_STATUSES = ['queued', 'running', 'done', 'failed', 'waiting_budget'];
export const CARRIER_RESEARCH_DECISIONS = ['approved', 'rejected'];
export const RECIPE_STATUSES = ['draft', 'active', 'broken', 'retired'];
export const READ_OUTCOMES = [
    /** Handed to the extension, no result yet (P3.8): the stale-claim sweep fails it `timeout` past its deadline. */
    'dispatched',
    'captured',
    'extracted',
    'applied',
    'empty',
    'not_found',
    'rejected',
    'failed',
];
export const READ_FAILURES = [
    'site_down',
    'anti_bot',
    'captcha',
    'recipe_broken',
    'timeout',
    'network',
    'model_lost',
    'human_needed',
    'other',
];
export const JOB_KINDS = [
    'read',
    'verify',
    'position_fallback',
    'recompute',
    'deliver',
    'notify',
    'repair_recipe',
    'maintenance',
];
export const JOB_STATES = ['queued', 'running', 'done', 'failed', 'cancelled'];
export const ALERT_SEVERITIES = ['blocking', 'warning', 'info'];
export const ALERT_ACTORS = ['data', 'manual', 'support', 'none'];
export const PAYLOAD_KINDS = ['full', 'position'];
export const DELIVERY_STATES = [
    'pending',
    'sending',
    'delivered',
    'rejected',
    'retry',
    'dead',
    'superseded',
    /** Owner's rule (16/10): Dokless writes are off (`outbound.dokless.enabled`); built, stored, never sent. */
    'shadow',
];
export const NOTIFICATION_CHANNELS = ['telegram', 'email', 'webhook'];
export const NOTIFICATION_STATES = ['pending', 'sent', 'suppressed', 'failed'];
/** Why a notification was not sent (05 §4.2, §4.4): a repeat, silenced, the night, folded into the next alert, no channel. */
export const NOTIFICATION_SUPPRESSED_REASONS = [
    'dedupe',
    'silence',
    'channel_off',
    'quiet_hours',
    'folded',
];
// Smaller enums named in the table descriptions of 03-database.md.
export const EVENT_PHASES = ['origin', 'sea', 'destination', 'problem'];
export const USER_ROLES = ['owner', 'operator', 'viewer'];
export const LOCALES = ['it', 'en'];
export const PASSAGE_STATES = ['open', 'restricted', 'closed'];
/** `sea_passages.state`, the state in force: `unknown` until the agent or a person said something (ADR 0075). */
export const PASSAGE_STATES_IN_FORCE = [...PASSAGE_STATES, 'unknown'];
export const SITE_HEALTH_STATES = ['ok', 'slow', 'down', 'anti_bot', 'captcha'];
export const PROPOSAL_STATUSES = ['running', 'proposed', 'applied', 'rejected', 'failed'];
export const EXTENSION_CLIENT_STATES = ['idle', 'busy', 'waiting_human'];
export const ACTIVITY_LEVELS = ['info', 'warn', 'error'];
export const NOTE_LEVELS = ['info', 'warn', 'error'];
/** The daily alignment with Dokless (P4.6): what a shipment's comparison concluded. */
export const ALIGNMENT_VERDICTS = [
    'aligned',
    'resend',
    'report',
    'not_comparable',
    'nothing_to_compare',
    'no_payload',
];
export const READ_MODES = ['recipe', 'model', 'direct_http'];
/** What a model call was for (`model_usage.purpose`, the daily budget). */
export const MODEL_USAGE_PURPOSES = [
    'reader',
    'navigation',
    'verify',
    'geocode',
    'repair',
    'passages',
    'carrier_research',
];
/**
 * How a model call is paid (`model_usage.billing`, ADR 0054): `api` (DeepSeek, the Anthropic API) counts
 * against the daily USD cap; `subscription` (Claude through the owner's Claude Code CLI) against the
 * daily call cap `models.cli_daily_calls`, its cost kept only as the CLI's estimate.
 */
export const MODEL_BILLINGS = ['api', 'subscription'];
export const transportModeSchema = z.enum(TRANSPORT_MODES);
export const dataSourceSchema = z.enum(DATA_SOURCES);
export const dataStatusSchema = z.enum(DATA_STATUSES);
export const shipmentStatusSchema = z.enum(SHIPMENT_STATUSES);
export const cargoStateSchema = z.enum(CARGO_STATES);
export const closedReasonSchema = z.enum(CLOSED_REASONS);
export const legStatusSchema = z.enum(LEG_STATUSES);
export const conveyanceKindSchema = z.enum(CONVEYANCE_KINDS);
export const carrierSearchKeySchema = z.enum(CARRIER_SEARCH_KEYS);
export const readFailureSchema = z.enum(READ_FAILURES);
export const alertSeveritySchema = z.enum(ALERT_SEVERITIES);
export const alertActorSchema = z.enum(ALERT_ACTORS);
export const userRoleSchema = z.enum(USER_ROLES);
//# sourceMappingURL=enums.js.map